import {EditOutlined} from '@ant-design/icons';
import {Button, Card, Form, Typography} from 'antd';
import {CardProps} from 'antd/es/card';
import {FormProps} from 'antd/es/form';
import {
  DocumentMetadataItem,
  DocumentStatus,
  useDocumentQuery,
  useUpdateDocumentMutation
} from 'api/generated';
import {AnchorLink, PageHeader, PropertyList, Spacer, Stack} from 'components';
import {DocumentStatusTag} from 'components/DocumentStatusTag';
import {Formik} from 'formik';
import {FormItem, Input, Select, Switch} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {useAccessLevel} from 'hooks/useAccessLevel';
import humanize from 'humanize-string';
import {getContentTypeIcon} from 'lib/contentTypeIcons';
import {formatBoolean, formatDate, formatTable} from 'lib/formaters';
import {
  documentCodes,
  documentErrorCodes,
  documentStatusLabels,
  documentTypeLabels
} from 'lib/labels';
import {nullify} from 'lib/utils';
import React, {useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {DocumentTitle} from 'routes/Account/Document/DocumentTitle';
import {RejectDocument} from 'routes/Account/Document/RejectDocument';
import {NotFound} from '../../NotFound';
import {ApproveDocument} from './ApproveDocument';
import {DocumentTimeline} from './DocumentTimeline';
import {DownloadDocument} from './DownloadDocument';

const {Text} = Typography;

const formProps: FormProps = {
  labelAlign: 'left',
  labelCol: {span: 6},
  wrapperCol: {span: 10},
  colon: false
};
export const Document: React.FC<RouteComponentProps<{accountId: string; documentId: string}>> = ({
  match
}) => {
  const {accountId, documentId} = match.params;
  const {isAdmin} = useAccessLevel();
  const [isEditing, setIsEditing] = useState(false);
  const {data, loading} = useDocumentQuery({
    variables: {id: documentId, accountId},
    context: {useApolloNetworkStatus: false}
  });

  const [updateDocument] = useUpdateDocumentMutation();

  const document = data?.document;

  if (!document && !loading) {
    return (
      <NotFound
        message={
          <>
            Sorry, document with id <strong>{documentId}</strong> does not exist
          </>
        }
      />
    );
  }

  const initialValues = {
    id: document?.id,
    accountId,
    name: document?.name,
    status: document?.status,
    internal: document?.internal,
    note: document?.note
  };

  const handleUpdateDocument = async (
    input: typeof initialValues,
    {setSubmitting}: FormikHelpers<typeof initialValues>
  ) => {
    try {
      await updateDocument({variables: {input: nullify(input)}});
    } finally {
      setIsEditing(false);
      setSubmitting(false);
    }
  };

  const cardProps: CardProps = {
    style: {marginBottom: 20},
    loading,
    size: 'small'
  };

  return (
    <>
      <PageHeader loading={loading}>
        <DocumentTitle document={document} />
        <DocumentStatusTag document={document} style={{marginLeft: 15}} />
        <Spacer />
        <DownloadDocument document={document} />
        <RejectDocument document={document} />
        <ApproveDocument document={document} />
      </PageHeader>
      <Card
        title="Document details"
        extra={
          isEditing || !isAdmin ? null : (
            <AnchorLink onClick={() => setIsEditing(true)}>
              <EditOutlined /> Edit data
            </AnchorLink>
          )
        }
        {...cardProps}>
        {isEditing && isAdmin ? (
          <Formik initialValues={initialValues} onSubmit={handleUpdateDocument}>
            {({submitForm, isSubmitting, values}) => (
              <Form {...formProps}>
                <FormItem name="name" label="Name">
                  <Input name="name" placeholder="Custom document name" />
                </FormItem>
                <FormItem name="status" label="Status">
                  <Select name="status" placeholder="Select status">
                    {Object.entries(documentStatusLabels).map(([key, value]) => (
                      <Select.Option value={key}>{value}</Select.Option>
                    ))}
                  </Select>
                </FormItem>
                {values.status === DocumentStatus.REJECTED && (
                  <FormItem name="statusCode" label="Status message">
                    <Select
                      name="statusCode"
                      placeholder="Select rejection reason"
                      style={{width: '100%'}}>
                      {Object.entries(documentErrorCodes).map(([key, value]) => (
                        <Select.Option value={key}>{value}</Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                )}
                <FormItem name="internal" label="Internal">
                  <Switch name="internal" />
                </FormItem>
                <FormItem name="note" label="Admin note">
                  <Input.TextArea name="note" placeholder="Custom admin note" rows={2} />
                </FormItem>
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  name="submit">
                  <Stack>
                    <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                    <Button type="primary" onClick={submitForm} loading={isSubmitting}>
                      Submit
                    </Button>
                  </Stack>
                </FormItem>
              </Form>
            )}
          </Formik>
        ) : (
          document && (
            <PropertyList
              omitEmpty
              properties={[
                {
                  label: 'Document id',
                  value: <Text copyable>{document?.id}</Text>
                },
                {label: 'Document type', value: documentTypeLabels[document?.type]},
                {
                  label: 'Content type',
                  value: (
                    <Stack gutter={5}>
                      {getContentTypeIcon(document?.contentType)}
                      <span>{document?.contentType}</span>
                    </Stack>
                  )
                },
                {label: 'Status', value: documentStatusLabels[document?.status]},
                {
                  label: 'Status message',
                  value: documentCodes[document?.statusCode as string]
                },
                {label: 'Internal', value: formatBoolean(document?.internal, 'Yes', 'No')},
                {label: 'Note', value: document?.note}
              ]}
            />
          )
        )}
      </Card>
      {document?.metadata && (
        <Card title="Extracted metadata" {...cardProps}>
          <PropertyList
            properties={document.metadata.map((item) => ({
              label: humanize(item.key),
              value: formatMetadataValue(item)
            }))}
          />
        </Card>
      )}
      {document && <DocumentTimeline document={document} />}
    </>
  );
};

function formatMetadataValue(item: DocumentMetadataItem) {
  const isDate = item.key.endsWith('Date');
  const formatValue = (value: string) => {
    if (isDate) {
      return formatDate(value, undefined, true);
    }
    return value;
  };
  if (!item.value || item.value.length === 0) return null;
  if (isDate || item.value[0].length >= 30) {
    return formatTable(item.value, (value) => [formatValue(value)]);
  } else {
    return item.value.map(formatValue).join(', ');
  }
}
